import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

/**
 * Breadcrumb trail plumbing.
 *
 * The app has exactly one breadcrumb and it lives in the header bar, so a
 * detail screen has to hand its trailing crumb up to the chrome. That crumb
 * is not derivable from the URL: `/members/:id` carries the SM2 UUID, while
 * the crumb shows the account number staff actually quote, which only
 * exists once the record has loaded.
 *
 * A context rather than a prop because `AppShell` renders `HeaderBar` as a
 * sibling of the routed screen, not as its parent - there is no prop path
 * between them.
 *
 * Contract: a screen calls `useSetBreadcrumbTrail(value)` and passes `null`
 * while loading. The hook clears the trail on unmount, so navigating away
 * can never leave a stale crumb pointing at the previous record.
 */

interface BreadcrumbContextValue {
  readonly trail: string | null
  readonly setTrail: (value: string | null) => void
}

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  trail: null,
  setTrail: () => undefined,
})

export function BreadcrumbProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [trail, setTrail] = useState<string | null>(null)
  const value = useMemo(() => ({ trail, setTrail }), [trail])
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>
}

/** Read the current trailing crumb. Used by `HeaderBar`. */
export function useBreadcrumbTrail(): string | null {
  return useContext(BreadcrumbContext).trail
}

/**
 * Publish a trailing crumb for as long as this screen is mounted.
 *
 * Pass `null` while the record is loading so the crumb does not flash a
 * placeholder.
 */
export function useSetBreadcrumbTrail(value: string | null): void {
  const { setTrail } = useContext(BreadcrumbContext)
  useEffect(() => {
    setTrail(value)
    return () => setTrail(null)
  }, [value, setTrail])
}
